import React from 'react';
import moment from 'moment';
import { usePreferences } from '../../contexts/PreferencesContext';
import {
  chatOptionKeys,
  getTranslatedOptionName,
  proctoringOptionKeys,
  simulationProctoringOptionKeys
} from './processoTranslationOptions';

const boolLabel = (value, yes, no) => value ? yes : no;
const statusTone = (value) => value ? 'success' : 'neutral';
const SummaryBadge = ({ tone = 'neutral', children }) => (
  <strong className={`config-summary-badge is-${tone}`}>{children}</strong>
);

const combineDateTime = (date, time) => {
  const dateMoment = moment(date);
  const timeMoment = moment(time);

  if (!dateMoment.isValid()) return moment();
  if (!timeMoment.isValid()) return dateMoment;

  return dateMoment.clone().hour(timeMoment.hour()).minute(timeMoment.minute()).second(0);
};

const formatDateTime = (date, time) => combineDateTime(date, time).format('DD/MM/YYYY HH:mm');

const getDuration = (state, t) => {
  if (state.type_time === 'restrict') {
    return state.tempo_prova
      ? `${state.tempo_prova} ${t('unit_minutes')}`
      : t('summary_define_time');
  }

  const minutes = combineDateTime(state.fim, state.hora_fim)
    .diff(combineDateTime(state.inicio, state.hora_inicio), 'minutes');

  if (!Number.isFinite(minutes)) return t('summary_not_informed');
  return `${Math.max(minutes, 0)} ${t('unit_minutes')}`;
};

const ProcessoConfigSummary = ({ state, user }) => {
  const { t } = usePreferences();
  const canConfigureOnline = user?.empresa?.online_proccess === true;
  const isOnline = state.online === true || state.online === 'true';
  const showOnlineOptions = canConfigureOnline && isOnline;
  const isInterview = state.is_interview === true || state.is_interview === 'true';
  const isSimulado = state.simulado === true || state.simulado === 'true';
  const hasBlocks = state.has_blocks === true || state.has_blocks === 'true';
  const examPerTime = state.exam_per_time === true || state.exam_per_time === 'true';
  const needPause = state.need_pause === true || state.need_pause === 'true';
  const mobile = state.mobile === true || state.mobile === 'true';
  const showRascunho = state.show_rascunho === true || state.show_rascunho === 'true';
  const fullScreen = state.full_screen === true || state.full_screen === 'true';
  const screenSharing = state.screen_sharing === true || state.screen_sharing === 'true';
  const requiredExternalCamera = state.required_external_camera === true || state.required_external_camera === 'true';
  const screenExtendedRestrict = state.screen_extended_retrict === true || state.screen_extended_retrict === 'true';
  const faceValidation = state.face_validation === true || state.face_validation === 'true';
  const showDocument = state.show_document === true || state.show_document === 'true';
  const faceValidationPeriodicaly = state.face_validation_periodicaly === true || state.face_validation_periodicaly === 'true';
  const restrictAccess = state.restrict_access === true || state.restrict_access === 'true';
  const fiscalizacaoOptions = isSimulado ? state.fiscalizacoes_simulado : state.fiscalizacoes;
  const fiscalizacaoKeys = isSimulado ? simulationProctoringOptionKeys : proctoringOptionKeys;
  const hasRules = (state.regras || '').replace(/<[^>]*>/g, '').trim().length > 0;
  const hasDescription = (state.descricao || '').trim().length > 0;
  const showFiscalizacao = showOnlineOptions && !isSimulado;
  const showChatType = showOnlineOptions && state.fiscalizacao === 'image';
  const showChatTime = showOnlineOptions && state.fiscalizacao !== 'none';
  const showAdvancedFiscalizacao = showOnlineOptions && state.fiscalizacao === 'all' && !mobile;
  const recordBeforeExam = showOnlineOptions && state.fiscalizacao === 'all' && !isSimulado &&
    (state.recording_start_before_enabled === true || state.recording_start_before_enabled === 'true') &&
    parseInt(state.recording_start_before_minutes || 0, 10) > 0;
  const showFaceValidationDetails = showAdvancedFiscalizacao && faceValidation;
  const showRestrictedTime = showOnlineOptions && state.type_time === 'restrict';

  return (
    <div className="config-sidebar">
      <div className="config-summary-card">
        <h5 className="config-summary-title">{t('process_summary_title')}</h5>

        <div className="config-summary-section">
          <h6>{t('summary_identification')}</h6>
          <div className="summary-block">
            <strong>{t('label_name')}</strong>
            <div>{state.name || t('summary_not_informed_yet')}</div>
          </div>
          <div className="summary-row">
            <span>{t('label_description')}</span>
            <SummaryBadge tone={statusTone(hasDescription)}>
              {hasDescription ? t('summary_filled_feminine') : t('status_pending')}
            </SummaryBadge>
          </div>
          {canConfigureOnline && (
            <div className="summary-row">
              <span>{t('label_rules')}</span>
              <SummaryBadge tone={isOnline ? statusTone(hasRules) : 'neutral'}>
                {isOnline
                  ? boolLabel(hasRules, t('summary_filled_plural'), t('summary_pending_plural'))
                  : t('summary_not_applicable')}
              </SummaryBadge>
            </div>
          )}
        </div>

        {canConfigureOnline && (
          <div className="config-summary-section">
            <h6>{t('summary_modality')}</h6>
            <div className="summary-row">
              <span>{t('summary_assessment')}</span>
              <SummaryBadge tone={isOnline ? 'success' : 'warning'}>
                {isOnline ? t('summary_online') : t('summary_in_person')}
              </SummaryBadge>
            </div>
            {showOnlineOptions && (
              <>
                <div className="summary-row">
                  <span>{t('summary_oral_assessment')}</span>
                  <SummaryBadge tone={statusTone(isInterview)}>
                    {boolLabel(isInterview, t('answer_yes'), t('answer_no'))}
                  </SummaryBadge>
                </div>
                <div className="summary-row">
                  <span>{t('summary_simulation')}</span>
                  <SummaryBadge tone={isSimulado ? 'warning' : 'neutral'}>
                    {boolLabel(isSimulado, t('summary_compatibility'), t('answer_no'))}
                  </SummaryBadge>
                </div>
                <div className="summary-row">
                  <span>{t('summary_block_exam')}</span>
                  <SummaryBadge tone={statusTone(hasBlocks)}>
                    {boolLabel(hasBlocks, t('answer_yes'), t('answer_no'))}
                  </SummaryBadge>
                </div>
              </>
            )}
          </div>
        )}

        <div className="config-summary-section">
          <h6>{t('summary_schedule')}</h6>
          <div className="summary-block">
            <strong>{t('summary_start')}</strong>
            <div>{formatDateTime(state.inicio, state.hora_inicio)}</div>
          </div>
          <div className="summary-block">
            <strong>{t('label_end')}</strong>
            <div>{formatDateTime(state.fim, state.hora_fim)}</div>
          </div>
        </div>

        {showOnlineOptions && (
          <div className="config-summary-section">
            <h6>{t('summary_time')}</h6>
            <div className="summary-row">
              <span>{t('legacy_modelo')}</span>
              <SummaryBadge tone={state.type_time === 'restrict' ? 'warning' : 'success'}>
                {state.type_time === 'restrict'
                  ? t('summary_restricted_after_access')
                  : t('summary_total_time')}
              </SummaryBadge>
            </div>
            {showRestrictedTime && (
              <div className="summary-row">
                <span>{t('summary_restricted_duration')}</span>
                <strong>{getDuration(state, t)}</strong>
              </div>
            )}
            <div className="summary-row">
              <span>{t('summary_time_per_question')}</span>
              <SummaryBadge tone={statusTone(examPerTime)}>
                {boolLabel(examPerTime, t('summary_required'), t('summary_disabled'))}
              </SummaryBadge>
            </div>
          </div>
        )}

        {showOnlineOptions && (
          <div className="config-summary-section">
            <h6>{t('summary_proctoring')}</h6>
            {showFiscalizacao && (
              <div className="summary-row">
                <span>{t('legacy_tipo')}</span>
                <SummaryBadge tone={state.fiscalizacao !== 'none' ? 'success' : 'neutral'}>
                  {getTranslatedOptionName(t, fiscalizacaoKeys, fiscalizacaoOptions, state.fiscalizacao)}
                </SummaryBadge>
              </div>
            )}
            {showChatType && (
              <div className="summary-row">
                <span>{t('summary_chat')}</span>
                <SummaryBadge tone={state.chat_type !== 'none' ? 'success' : 'neutral'}>{getTranslatedOptionName(t, chatOptionKeys, state.chat_types, state.chat_type)}</SummaryBadge>
              </div>
            )}
            {showChatTime && (
              <div className="summary-row">
                <span>{t('summary_chat_before_exam')}</span>
                <SummaryBadge tone="warning">{`${state.chat_time || 0} ${t('unit_minutes_short')}`}</SummaryBadge>
              </div>
            )}
            <div className="summary-row">
              <span>{t('summary_full_screen')}</span>
              <SummaryBadge tone={statusTone(fullScreen)}>
                {boolLabel(fullScreen, t('summary_required_feminine'), t('summary_optional'))}
              </SummaryBadge>
            </div>
            <div className="summary-row">
              <span>{t('summary_multiple_monitors')}</span>
              <SummaryBadge tone={screenExtendedRestrict ? 'success' : 'danger'}>
                {boolLabel(screenExtendedRestrict, t('summary_blocked_plural'), t('summary_allowed_plural'))}
              </SummaryBadge>
            </div>
            {showAdvancedFiscalizacao && (
              <>
                <div className="summary-row">
                  <span>{t('summary_share_screen')}</span>
                  <SummaryBadge tone={statusTone(screenSharing)}>
                    {boolLabel(screenSharing, t('summary_required'), t('summary_not_required'))}
                  </SummaryBadge>
                </div>
                <div className="summary-row">
                  <span>{t('summary_external_camera')}</span>
                  <SummaryBadge tone={statusTone(requiredExternalCamera)}>
                    {boolLabel(requiredExternalCamera, t('summary_required_feminine'), t('summary_not_required'))}
                  </SummaryBadge>
                </div>
                <div className="summary-row">
                  <span>{t('summary_record_before_exam')}</span>
                  <SummaryBadge tone={statusTone(recordBeforeExam)}>
                    {recordBeforeExam
                      ? `${state.recording_start_before_minutes} ${t('unit_minutes_short')}`
                      : t('summary_from_exam_start')}
                  </SummaryBadge>
                </div>
              </>
            )}
          </div>
        )}

        {showAdvancedFiscalizacao && (
          <div className="config-summary-section">
            <h6>{t('summary_validations')}</h6>
            <div className="summary-row">
              <span>{t('summary_face_microphone')}</span>
              <SummaryBadge tone={statusTone(faceValidation)}>
                {boolLabel(faceValidation, t('summary_on_entry'), t('summary_disabled'))}
              </SummaryBadge>
            </div>
            <div className="summary-row">
              <span>{t('summary_document')}</span>
              <SummaryBadge tone={statusTone(showDocument)}>
                {boolLabel(showDocument, t('summary_on_entry'), t('summary_disabled'))}
              </SummaryBadge>
            </div>
            {showFaceValidationDetails && (
              <>
                <div className="summary-row">
                  <span>{t('summary_release_button')}</span>
                  <SummaryBadge tone="warning">{`${state.request_time || 0} ${t('summary_minutes_before')}`}</SummaryBadge>
                </div>
                <div className="summary-row">
                  <span>{t('summary_periodic_validation')}</span>
                  <SummaryBadge tone={statusTone(faceValidationPeriodicaly)}>
                    {faceValidationPeriodicaly
                      ? `${state.time_validation_periodicaly || 0}${t('unit_seconds_short')} ${t('summary_without_recognition')}`
                      : t('summary_disabled_feminine')}
                  </SummaryBadge>
                </div>
              </>
            )}
          </div>
        )}

        {showOnlineOptions && (
          <div className="config-summary-section">
            <h6>{t('summary_candidate_resources')}</h6>
            <div className="summary-row">
              <span>{t('summary_mobile')}</span>
              <SummaryBadge tone={mobile ? 'success' : 'danger'}>
                {boolLabel(mobile, t('summary_allowed'), t('summary_blocked'))}
              </SummaryBadge>
            </div>
            <div className="summary-row">
              <span>{t('summary_pauses')}</span>
              <SummaryBadge tone={needPause ? 'success' : 'danger'}>
                {boolLabel(needPause, t('summary_allowed_feminine_plural'), t('summary_blocked_feminine_plural'))}
              </SummaryBadge>
            </div>
            <div className="summary-row">
              <span>{t('summary_scratchpad')}</span>
              <SummaryBadge tone={statusTone(showRascunho)}>
                {boolLabel(showRascunho, t('summary_available'), t('summary_unavailable'))}
              </SummaryBadge>
            </div>
          </div>
        )}

        {showOnlineOptions && (
          <div className="config-summary-section">
            <h6>{t('summary_access')}</h6>
            <div className="summary-row">
              <span>{t('summary_time_restriction')}</span>
              <SummaryBadge tone={statusTone(restrictAccess)}>
                {boolLabel(restrictAccess, t('summary_active_feminine'), t('summary_no_restriction'))}
              </SummaryBadge>
            </div>
            {restrictAccess && (
              <div className="summary-block">
                <strong>{t('summary_allowed_period')}</strong>
                <div>{formatDateTime(state.inicio_access, state.hora_inicio_access)}</div>
                <div>{t('summary_until')} {formatDateTime(state.fim_access, state.hora_fim_access)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessoConfigSummary;
